<template>
  <div id="digital-garage-iframe">

    <b-card v-if="!authorized">
      <!-- Credit Card Details Form -->
      <b-form @submit.prevent="submitForm">
        <!-- Card Number Input -->
        <label>
          <b>
            <!-- Card Information -->
            {{ $t("visit-information-form.card_information") }}
          </b>
        </label>
        <b-form-group
          label-for="cardNumber"
          label-cols-sm="3"
          label-size="sm"
          label-class="font-weight-medium"
          :label='$t("visit-information-form.card_number")'
        >
          <b-input
            id="cardNumber"
            v-model="formData.cardNumber"
            @input="handleInput('cardNumber')"
            @keypress="validateKeyPress($event, 'cardNumber')"
            :class="{'is-invalid': inputHasError('cardNumber')}"
            placeholder='0000 0000 0000 0000'
            type="text"
          ></b-input>
        </b-form-group>

        <!-- Expiry Date and CVC Inputs -->
        <b-row>
          <!-- Expiry Date Input -->
          <b-col cols="6">
            <b-form-group
              label-for="expiryDate"
              label-size="sm"
              label-class="font-weight-medium"
              :label='$t("visit-information-form.digital_garage_card_expiry_date")'
            >
              <b-input
                id="expiryDate"
                v-model="formData.expiryDate"
                @input="handleInput('expiryDate')"
                @keypress="validateKeyPress($event, 'expiryDate')"
                :class="{'is-invalid': inputHasError('expiryDate')}"
                placeholder="00/00"
                type="text"
                maxlength="4"
                 v-b-tooltip.hover.invalid="tooltipContent"
              ></b-input>
            </b-form-group>
          </b-col>

          <!-- CVC Input -->
          <b-col cols="6">
            <b-form-group
              label-for="cvc"
              label-size="sm"
              label-class="font-weight-medium"
              :label='$t("visit-information-form.card_cvc")'
            >
              <b-input
                id="cvc"
                v-model="formData.cvc"
                @input="handleInput('cvc')"
                @keypress="validateKeyPress($event, 'cvc')"
                :class="{'is-invalid': inputHasError('cvc')}"
                placeholder="000"
                type="text"
                maxlength="3"
              ></b-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
         <b-col>
            <b-form-group
               label-for="cardHolderName"
               label-cols-sm="3"
               label-size="sm"
               label-class="font-weight-medium"
               :label='$t("visit-information-form.card_holder_text_name")'
            >
               <b-input
                  id="cardHolderName"
                  v-model="formData.cardHolderName"
                  :placeholder='$t("visit-information-form.card_holder_text_name")'
                  type="text"
               ></b-input>
            </b-form-group>
         </b-col>
        </b-row>

        <b-row>
          <b-col>
            <b-form-group>
              <div class="d-flex justify-content-between align-items-center py-3 border-bottom">
                <span class="mr-2">{{ $t("visit-information-form.amount") }}:</span>
                <span>
                  <b class="mr-2">{{ totalAmount }} {{ configs.currency }}</b>
                </span>
              </div>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Error message -->
        <div v-if="errors" class="pb-4 text-center">
          <span class="text-danger w-full mx-auto">{{ errors }}</span>
        </div>

        <div class="w-full text-center">
          <button type="submit" class="btn btn-primary btn-w-lg ml-3 mx-auto" :disabled="!isFormValid">{{ $t("visit-information-form.authorized") }}</button>
        </div>
      </b-form>
    </b-card>

    <!-- Authorized message in iframe -->
    <template v-else>
      <b-row>
        <b-col>
          <div class="d-flex flex-column align-items-center justify-content-center py-4">
            <div class="processing-text"><b>{{ $t("visit-information-form.card_info_authorized") }}</b></div>
            <div class="mx-auto">
              <i class="fas fa-check-circle text-primary py-4" style="font-size: 8rem;"></i>
            </div>
            <button type="button" class="btn btn-primary btn-w-lg ml-3 mx-auto" @click="backToForm">{{ $t("visit-information-form.next") }}</button>
          </div>
        </b-col>
      </b-row>
    </template>

    <b-modal
      id="processing-modal"
      ref="loadingModal"
      hide-header hide-footer no-close-on-esc no-close-on-backdrop centered>
      <div class="text-center">
        <div class="processing-text py-2">
          <b>{{ $t("visit-information-form.processing_msg") }}</b>
        </div>
        <i class="fas fa-spinner fa-spin processing-icon py-3" style="font-size: 3rem;"></i>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Axios from 'axios';
import _typy from 'typy';
import { v4 as uuidv4 } from 'uuid';
import { mapActions, mapState } from "vuex";

export default {
   name: 'DigitalGaragePaymentIframe',
   data() {
      return {
         formData: {
            cardNumber: '',
            expiryDate: '',
            cvc: '',
            cardHolderName: ''
         },
         totalAmount: '',
         errors: '',
         authorized: false,
         configs: {},
      };
   },
   computed: {
      isFormValid() {
         return this.formData.cardNumber && this.formData.expiryDate && this.formData.cvc && this.formData.cardHolderName;
      },
      tooltipContent() {
         if (this.inputHasError('expiryDate')) {
            return this.$t('visit-information-form.digital_garage_card_expiry_date_error');
         }
         return '';
      }
   },
   async mounted() {
      console.log(this.$route.fullPath);
      const returnedError = this.$route.query?.showError ?? "";
      // const isSuccess = this.$route.query?.mpiStatus ?? 0;
      const isSuccess = this.$route.query?.success ?? 0;
      console.log("payment success? ",isSuccess);
      
      const configsQueryParams = this.$route?.query?.configs ?? "";
      this.configs = configsQueryParams !== "" ? JSON.parse(atob(configsQueryParams)) : "";
      console.log("this.configs ",this.configs);
      
      this.totalAmount = (this.configs.net_charge > 0) ? this.configs.net_charge : '';

      if (returnedError) {
         this.authorized = false;
         this.errors = this.$t('visit-information-form.error_occur');
         window.parent.postMessage({ action: 'endProcessing' }, '*');
      }

      if(isSuccess == "1") {
         console.log("Success");
         this.authorized = true;
      }
   },

   created() {
      const urlParams = new URLSearchParams(window.location.search);
      const lang = urlParams.get('lang');
      if (lang) {
         this.$i18n.locale = lang;
      }
   },
   methods: {
      submitForm() {
         this.tokenize(this.formData)
         window.parent.postMessage({ action: 'startProcessing' }, '*');
      },
      async tokenize(data) {
         this.errors = '';
         this.$refs.loadingModal.show();
         const monthSubStr = data.expiryDate.substring(0,2);
         const yearSubStr = data.expiryDate.substring(2,4);
         const newCardExpiryDate = `${monthSubStr}/${yearSubStr}`;
         const tokenKey = 
            this.configs.entry_method === 'postpaid' 
            ? process.env.VUE_APP_DIGITAL_GARAGE_POSTPAID_TOKEN_API_KEY 
            : process.env.VUE_APP_DIGITAL_GARAGE_PREPAID_TOKEN_API_KEY;

         const card = {
            // card_number: '4111111111111111', // no challenge but success result
            // card_number: '5111111111111118', // with challenge but result failed
            // card_number: '5105105105105100', // with challenge but result failed
            // card_number: '378282246310005', // with challenge but result failed
            // card_number: '377752749896404', // with challenge but success result
            // card_expire: '04/26',
            // security_code: '424',
            // cardholder_name: guestName,
            cardholder_name: data.cardHolderName,
            card_number: data.cardNumber,
            card_expire: newCardExpiryDate,
            security_code: data.cvc,
            token_api_key: tokenKey
         };

         await Axios
            .post("https://api3.veritrans.co.jp/4gtoken", card)
            .then((response) => {
               if(_typy(response.data, 'token').isDefined) {
                  let tokenizeCard = {
                     token: response.data.token,
                     return_url: this.$route.fullPath,
                     pms_reservation_no: this.configs.rsv_no,
                     pass: this.configs.pass,
                     pms_code: this.configs.pms_code,
                     facility_id: this.configs.facility_id,
                     currency: this.configs.currency,
                     entry_method: this.configs.entry_method
                  }
                  
                  // console.log('tokenizeCard ',tokenizeCard);
                  this.mpiCardAuthorized(tokenizeCard);
                  // this.$refs.loadingModal.hide();
               }
            })
            .catch(error => {
               window.parent.postMessage({ action: 'endProcessing' }, '*');
               this.errors = this.$t('visit-information-form.error_occur');
               this.$refs.loadingModal.hide();
            });
            // vm.$refs.loadingModal.hide();
      },
      async mpiCardAuthorized(tokenizeCard) {
         let vm = this;
         console.log("start authorizing card");
         await this.$lionheart
            .post("/v2/dg/payment-card-authorized", tokenizeCard)
            .then((response) => {
               console.log('card authorized response data? ',response.data?.data);
               const isFailedStatus = response.data?.data?.mstatus ?? 'failed';
               console.log('isFailedStatus ',isFailedStatus);
               if (isFailedStatus === 'failure') {
                  this.$refs.loadingModal.hide();
                  window.parent.postMessage({ action: 'endProcessing' }, '*');
                  vm.errors = vm.$t('visit-information-form.error_occur');
               } else {
                  const authURL = response.data?.data?.authURL ?? null;
                  console.log('authURL ',authURL);
                  
                  if (authURL) {
                     return window.location.href = authURL;
                  } else {
                     vm.authorized = true;
                     // this.$refs.loadingModal.hide();
                  }
               }

               // default error
               this.$refs.loadingModal.hide();
               window.parent.postMessage({ action: 'endProcessing' }, '*');
               vm.errors = vm.$t('visit-information-form.error_occur');
               return;
            }).catch((error) => {
               console.log(error);
               this.errors = this.$t('visit-information-form.error_occur');
               this.$refs.loadingModal.hide();
            })
      },
      handleInput(field) {
         this.formData[field] = this.formData[field].replace(/\D/g, '').trim();

         if (field === 'expiryDate') {
            if (this.formData.expiryDate.length > 4) {
               this.formData.expiryDate = this.formData.expiryDate.slice(0, 4);
            }
         }

      },
      validateKeyPress(event, field) {
         const keyCode = event.keyCode || event.which;
         const keyValue = String.fromCharCode(keyCode);

         if (!/^\d+$/.test(keyValue)) {
            event.preventDefault();
         }
      },
      inputHasError(field) {
         if (field === 'expiryDate') {
            const expiryDate = this.formData.expiryDate.trim();
            const currentYear = new Date().getFullYear() % 100;
            const month = parseInt(expiryDate.substring(0, 2), 10);
            const year = parseInt(expiryDate.substring(2, 4), 10);
            // const currentMonth = new Date().getMonth() + 1;
            // TODO: need to validate also the expiration month with year using this statement :if (year < currentYear|| (year === currentYear && month < currentMonth) || month < 1 || month > 12)

            if (year < currentYear || month < 1 || month > 12) {
               return true;
            }
         }

         return false;
      },
      backToForm() {
         //window.parent.postMessage({ action: 'closeModal', tokenizeCard: this.fincodeObj.tokens[1].token }, '*'); // Close the entire modal
         window.parent.postMessage({ action: 'closeModal', tokenizeCard: '' }, '*'); // Close the entire modal
      }
   }
};
</script>

<style lang="scss" scoped>
$theme-colors: (
  "primary": #1A91D5,
);

#fincode-iframe {
  font-family: "Poppins", sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
}

button {
  background-color: #1A91D5;
  border: none;
}

.processing-text {
  font-size: 18px;
  font-family: "Poppins", sans-serif;
}

.is-invalid {
  border-color: red !important;
}

.card > .card-body > form > .form-row > .card-label {
  color: #aaa !important;
  font-weight: 800 !important;
}

.tooltip {
  background-color: #fff !important;
  color: #333333 !important;
  font-size: 12px !important;
  border-radius: 5px !important;
  padding: 5px 10px !important;
  font-family: Poppins;
}

.tooltip-inner {
  background-color: #fff !important;
  color: #333333 !important;
}
.tooltip-arrow::before {
  border-top-color: #fff !important;
}

.tooltip-arrow::before {
  border-top-color: #fff !important;
}
</style>
